// src/pages/enterprise/EnterpriseFeesTab.js
//
// Per-org orchestration fee configuration UI.
// V1: local React state + mocked data. T6 will wire the persistence layer.
//
// Override modes:
//   platform_default | flat_override | spend_tier | time_ramp | spend_and_time
//
// Wallet load remains 1:1 (no margin at load).
// Margin is collected only on spend (Highnote settlement, card-on-file
// pre-auth, billing recovery invoices).

import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Divider,
    Button,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Switch,
    Slider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    Tooltip,
    Chip
} from '@mui/material';
import { styled } from '@mui/system';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import HistoryIcon from '@mui/icons-material/History';
import SaveIcon from '@mui/icons-material/Save';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScienceIcon from '@mui/icons-material/Science';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import SharedModal from '../../components/common/Modal';
import { InfoIcon as SharedInfoIcon } from '../../components/common/InfoIcon';

// -----------------------------------------------------------------------------
//  CONSTANTS
// -----------------------------------------------------------------------------

const PLATFORM_DEFAULT_RATE = 0.15;

// Clickable preset only. NEVER applied automatically. The admin must explicitly
// click "Apply suggested tiers" to populate the band table with these values.
const SUGGESTED_TIERS = [
    { floorUSD: 0,        ceilingUSD: 500000,    rate: 0.15 },
    { floorUSD: 500000,   ceilingUSD: 2000000,   rate: 0.13 },
    { floorUSD: 2000000,  ceilingUSD: 10000000,  rate: 0.11 },
    { floorUSD: 10000000, ceilingUSD: 25000000,  rate: 0.09 },
    { floorUSD: 25000000, ceilingUSD: null,      rate: 0.08 }
];

const MODE_OPTIONS = [
    { value: 'platform_default', label: 'Platform default (15%, hidden in invoices)' },
    { value: 'flat_override',    label: 'Flat override (custom flat rate)' },
    { value: 'spend_tier',       label: 'Spend-based tiers (rate drops as annual spend increases)' },
    { value: 'time_ramp',        label: 'Time-based ramp (rate changes by contract year)' },
    { value: 'spend_and_time',   label: 'Spend + time (whichever is lower applies)' }
];

// -----------------------------------------------------------------------------
//  MOCK ORG DATA (V1)
// -----------------------------------------------------------------------------
// TODO[T6]: replace with GET /api/orchestration-fee/:orgId

const MOCK_ORG = {
    orgName: "Foundation Capital",
    orgId: "65fc8b2a91d4e2c7f1234567",
    contractAnniversary: "2026-01-15",

    orchestrationFee: {
        mode: "platform_default",
        effectiveRate: null,
        flatRate: null,
        spendTiers: [],
        timeRamp: [],
        currentContractYearStart: "2026-01-15",
        currentContractYearSpendUSD: 0,
        lifetimeSpendUSD: 0,
        changeLog: []
    },

    platformDefaultRate: PLATFORM_DEFAULT_RATE
};

// -----------------------------------------------------------------------------
//  STYLED
// -----------------------------------------------------------------------------

const SectionCard = styled(Card)({
    backgroundColor: "#111",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "#fff",
    borderRadius: 14,
    padding: "0.8rem",
    marginBottom: "1.5rem"
});

const inputSx = {
    backgroundColor: "#161616",
    color: "#fff",
    '& .MuiOutlinedInput-root': {
        color: "#fff",
        '& fieldset': { borderColor: '#333' },
        '&:hover fieldset': { borderColor: '#444' },
        '&.Mui-focused fieldset': { borderColor: '#12c099' }
    },
    '& input': { color: '#fff' }
};

const tableHeadSx = {
    color: "#888",
    fontWeight: 600,
    borderBottom: "1px solid rgba(255,255,255,0.1)"
};

const tableCellSx = {
    color: "#fff",
    borderBottom: "1px solid rgba(255,255,255,0.06)"
};

const mintButtonSx = {
    backgroundColor: "#12c099",
    color: "#000",
    fontWeight: 700,
    textTransform: "none",
    '&:hover': { backgroundColor: "#0ea884" }
};

const outlinedMintSx = {
    borderColor: "#12c099",
    color: "#12c099",
    textTransform: "none",
    '&:hover': { borderColor: "#0ea884", backgroundColor: "rgba(18,192,153,0.08)" }
};

// -----------------------------------------------------------------------------
//  HELPERS
// -----------------------------------------------------------------------------

const fmtUSD = (n) => {
    if (n === null || n === undefined || isNaN(n)) return "$0";
    return "$" + Number(n).toLocaleString();
};

const fmtUSDCompact = (n) => {
    if (n === null || n === undefined) return "∞";
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${n}`;
};

const todayISO = () => new Date().toISOString();

// Resolve the spend-tier rate. Returns { rate, reason } or null if no band matches.
function resolveSpendTier(tiers, spendUSD) {
    if (!Array.isArray(tiers) || tiers.length === 0) return null;
    for (const tier of tiers) {
        const floor = Number(tier.floorUSD) || 0;
        const ceiling = tier.ceilingUSD === null || tier.ceilingUSD === "" || tier.ceilingUSD === undefined
            ? null
            : Number(tier.ceilingUSD);
        const matchesFloor = spendUSD >= floor;
        const matchesCeiling = ceiling === null || spendUSD < ceiling;
        if (matchesFloor && matchesCeiling) {
            return {
                rate: Number(tier.rate),
                reason: `spend_tier band ${fmtUSDCompact(floor)}-${fmtUSDCompact(ceiling)}`
            };
        }
    }
    return null;
}

// Resolve the time-ramp rate. Returns { rate, reason } or null.
function resolveTimeRamp(ramps, contractYear) {
    if (!Array.isArray(ramps) || ramps.length === 0) return null;
    for (const ramp of ramps) {
        const start = Number(ramp.yearStart) || 1;
        const end = ramp.yearEnd === null || ramp.yearEnd === "" || ramp.yearEnd === undefined
            ? null
            : Number(ramp.yearEnd);
        const matchesStart = contractYear >= start;
        const matchesEnd = end === null || contractYear <= end;
        if (matchesStart && matchesEnd) {
            return {
                rate: Number(ramp.rate),
                reason: `time_ramp year ${contractYear}`
            };
        }
    }
    return null;
}

// Master resolver across all modes. Always returns { rate, reason, alt? }.
function resolveRate(config, hypotheticalSpendUSD, contractYear) {
    const { mode, flatRate, spendTiers, timeRamp } = config;

    if (mode === 'platform_default') {
        return { rate: PLATFORM_DEFAULT_RATE * 100, reason: "Platform default (15%)" };
    }

    if (mode === 'flat_override') {
        const r = Number(flatRate);
        if (isNaN(r)) return { rate: PLATFORM_DEFAULT_RATE * 100, reason: "Flat override unset, falling back to platform default" };
        return { rate: r, reason: `Flat override ${r}%` };
    }

    if (mode === 'spend_tier') {
        const result = resolveSpendTier(spendTiers, hypotheticalSpendUSD);
        if (!result) return { rate: PLATFORM_DEFAULT_RATE * 100, reason: "No spend tier matched, falling back to platform default" };
        return { rate: result.rate, reason: result.reason };
    }

    if (mode === 'time_ramp') {
        const result = resolveTimeRamp(timeRamp, contractYear);
        if (!result) return { rate: PLATFORM_DEFAULT_RATE * 100, reason: "No ramp band matched, falling back to platform default" };
        return { rate: result.rate, reason: result.reason };
    }

    if (mode === 'spend_and_time') {
        const sp = resolveSpendTier(spendTiers, hypotheticalSpendUSD);
        const tr = resolveTimeRamp(timeRamp, contractYear);
        const spRate = sp ? sp.rate : PLATFORM_DEFAULT_RATE * 100;
        const trRate = tr ? tr.rate : PLATFORM_DEFAULT_RATE * 100;
        const winnerIsSpend = spRate <= trRate;
        return {
            rate: winnerIsSpend ? spRate : trRate,
            reason: winnerIsSpend
                ? `spend_and_time (spend wins): ${sp ? sp.reason : 'platform default'}`
                : `spend_and_time (time wins): ${tr ? tr.reason : 'platform default'}`,
            alt: {
                spendRate: spRate,
                timeRate: trRate,
                spendReason: sp ? sp.reason : 'platform default',
                timeReason: tr ? tr.reason : 'platform default'
            }
        };
    }

    return { rate: PLATFORM_DEFAULT_RATE * 100, reason: "Unknown mode, falling back to platform default" };
}

// Compute the current contract year given the anniversary.
function currentContractYear(anniversaryISO) {
    if (!anniversaryISO) return 1;
    const start = new Date(anniversaryISO);
    const now = new Date();
    let years = now.getFullYear() - start.getFullYear();
    const monthDiff = now.getMonth() - start.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < start.getDate())) {
        years -= 1;
    }
    return Math.max(1, years + 1);
}

// Validation. Returns { errors: [], warnings: [] }.
function validateConfig(config) {
    const errors = [];
    const warnings = [];
    const { mode, flatRate, spendTiers, timeRamp } = config;

    if (mode === 'flat_override') {
        const r = Number(flatRate);
        if (isNaN(r) || r <= 0 || r >= 100) {
            errors.push("Flat rate must be a number greater than 0 and less than 100.");
        }
    }

    if (mode === 'spend_tier' || mode === 'spend_and_time') {
        const tiers = spendTiers || [];
        if (tiers.length === 0) {
            errors.push("Add at least one spend tier band, or switch to a different mode.");
        }
        tiers.forEach((t, i) => {
            const r = Number(t.rate);
            if (isNaN(r) || r <= 0 || r >= 100) {
                errors.push(`Spend tier #${i + 1}: rate must be between 0 and 100.`);
            }
            const floor = Number(t.floorUSD);
            const ceiling = t.ceilingUSD === null || t.ceilingUSD === "" ? null : Number(t.ceilingUSD);
            if (ceiling !== null && floor >= ceiling) {
                errors.push(`Spend tier #${i + 1}: floor must be less than ceiling.`);
            }
        });
        // Overlap detection: sort by floor, check that ceiling[i] <= floor[i+1]
        const sorted = [...tiers]
            .map((t, idx) => ({ ...t, idx }))
            .sort((a, b) => Number(a.floorUSD) - Number(b.floorUSD));
        for (let i = 0; i < sorted.length - 1; i++) {
            const a = sorted[i];
            const b = sorted[i + 1];
            const aCeil = a.ceilingUSD === null || a.ceilingUSD === "" ? Infinity : Number(a.ceilingUSD);
            const bFloor = Number(b.floorUSD);
            if (aCeil > bFloor) {
                errors.push(`Spend tiers overlap between band #${a.idx + 1} and band #${b.idx + 1}.`);
            } else if (aCeil < bFloor) {
                warnings.push(`Gap in spend tiers between band #${a.idx + 1} (ends ${fmtUSDCompact(aCeil)}) and band #${b.idx + 1} (starts ${fmtUSDCompact(bFloor)}). Contiguous bands recommended.`);
            }
        }
        // Last band convention: should have null/blank ceiling.
        if (sorted.length > 0) {
            const last = sorted[sorted.length - 1];
            if (last.ceilingUSD !== null && last.ceilingUSD !== "") {
                warnings.push(`Last spend tier should have a blank ceiling (∞) to cover unbounded spend.`);
            }
        }
    }

    if (mode === 'time_ramp' || mode === 'spend_and_time') {
        const ramps = timeRamp || [];
        if (ramps.length === 0) {
            errors.push("Add at least one time-ramp band, or switch to a different mode.");
        }
        ramps.forEach((r, i) => {
            const rate = Number(r.rate);
            if (isNaN(rate) || rate <= 0 || rate >= 100) {
                errors.push(`Time ramp #${i + 1}: rate must be between 0 and 100.`);
            }
            const ys = Number(r.yearStart);
            const ye = r.yearEnd === null || r.yearEnd === "" ? null : Number(r.yearEnd);
            if (ye !== null && ys > ye) {
                errors.push(`Time ramp #${i + 1}: year start must be ≤ year end.`);
            }
        });
        // Overlap and sequential check.
        const sorted = [...ramps]
            .map((r, idx) => ({ ...r, idx }))
            .sort((a, b) => Number(a.yearStart) - Number(b.yearStart));
        for (let i = 0; i < sorted.length - 1; i++) {
            const a = sorted[i];
            const b = sorted[i + 1];
            const aEnd = a.yearEnd === null || a.yearEnd === "" ? Infinity : Number(a.yearEnd);
            const bStart = Number(b.yearStart);
            if (aEnd >= bStart) {
                errors.push(`Time ramps overlap between band #${a.idx + 1} and band #${b.idx + 1}.`);
            }
        }
    }

    return { errors, warnings };
}

// -----------------------------------------------------------------------------
//  COMPONENT
// -----------------------------------------------------------------------------

export default function EnterpriseFeesTab() {

    // ---------------- state ----------------
    const [mode, setMode] = useState(MOCK_ORG.orchestrationFee.mode);
    const [flatRate, setFlatRate] = useState(MOCK_ORG.orchestrationFee.flatRate ?? "");
    const [spendTiers, setSpendTiers] = useState(MOCK_ORG.orchestrationFee.spendTiers || []);
    const [timeRamp, setTimeRamp] = useState(MOCK_ORG.orchestrationFee.timeRamp || []);
    const [contractAnniversary, setContractAnniversary] = useState(MOCK_ORG.contractAnniversary);
    const [changeLog, setChangeLog] = useState(MOCK_ORG.orchestrationFee.changeLog || []);

    const currentContractYearSpendUSD = MOCK_ORG.orchestrationFee.currentContractYearSpendUSD;
    const lifetimeSpendUSD = MOCK_ORG.orchestrationFee.lifetimeSpendUSD;

    // ---------------- simulator ----------------
    const [simSpend, setSimSpend] = useState(3000000);

    // ---------------- save / dialog state ----------------
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [applySuggestedOpen, setApplySuggestedOpen] = useState(false);
    const [saveReason, setSaveReason] = useState("");
    const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

    // ---------------- derived ----------------
    const config = useMemo(() => ({
        mode,
        flatRate: flatRate === "" ? null : Number(flatRate),
        spendTiers,
        timeRamp
    }), [mode, flatRate, spendTiers, timeRamp]);

    const contractYear = useMemo(() => currentContractYear(contractAnniversary), [contractAnniversary]);

    const resolvedCurrent = useMemo(
        () => resolveRate(config, currentContractYearSpendUSD, contractYear),
        [config, currentContractYearSpendUSD, contractYear]
    );

    const validation = useMemo(() => validateConfig(config), [config]);

    const simResult = useMemo(
        () => resolveRate(config, Number(simSpend) || 0, contractYear),
        [config, simSpend, contractYear]
    );

    const simulatedFee = useMemo(() => {
        const rate = Number(simResult.rate) / 100;
        return Math.round((Number(simSpend) || 0) * rate);
    }, [simResult, simSpend]);

    // ---------------- handlers ----------------

    const handleAddSpendBand = () => {
        const last = spendTiers[spendTiers.length - 1];
        const newFloor = last ? (Number(last.ceilingUSD) || 0) : 0;
        setSpendTiers([...spendTiers, { floorUSD: newFloor, ceilingUSD: "", rate: "" }]);
    };

    const handleRemoveSpendBand = (idx) => {
        setSpendTiers(spendTiers.filter((_, i) => i !== idx));
    };

    const handleSpendBandChange = (idx, field, value) => {
        setSpendTiers(spendTiers.map((t, i) =>
            i === idx ? { ...t, [field]: value } : t
        ));
    };

    const handleAddTimeBand = () => {
        const last = timeRamp[timeRamp.length - 1];
        const newStart = last ? (Number(last.yearEnd) || Number(last.yearStart) || 0) + 1 : 1;
        setTimeRamp([...timeRamp, { yearStart: newStart, yearEnd: "", rate: "" }]);
    };

    const handleRemoveTimeBand = (idx) => {
        setTimeRamp(timeRamp.filter((_, i) => i !== idx));
    };

    const handleTimeBandChange = (idx, field, value) => {
        setTimeRamp(timeRamp.map((r, i) =>
            i === idx ? { ...r, [field]: value } : r
        ));
    };

    const openApplySuggested = () => {
        if (spendTiers.length > 0) {
            setApplySuggestedOpen(true);
        } else {
            applySuggested();
        }
    };

    const applySuggested = () => {
        setSpendTiers(SUGGESTED_TIERS.map((t) => ({
            floorUSD: t.floorUSD,
            ceilingUSD: t.ceilingUSD === null ? "" : t.ceilingUSD,
            rate: t.rate * 100
        })));
        setApplySuggestedOpen(false);
        setToast({ open: true, severity: 'info', message: 'Suggested tier preset applied.' });
    };

    const handleSaveClick = () => {
        if (validation.errors.length > 0) {
            setToast({
                open: true,
                severity: 'error',
                message: `Cannot save: ${validation.errors[0]}`
            });
            return;
        }
        setConfirmOpen(true);
    };

    const handleSaveConfirm = () => {
        // TODO[T6]: PUT /api/orchestration-fee/:orgId with config payload
        const entry = {
            timestamp: todayISO(),
            actor: "admin (current user TBD when auth wired)",
            mode,
            summary: describeChange(config),
            reason: saveReason || "(no reason given)"
        };
        setChangeLog([entry, ...changeLog]);
        setConfirmOpen(false);
        setSaveReason("");
        setToast({ open: true, severity: 'success', message: 'Configuration saved.' });
    };

    // ---------------- render ----------------

    return (
        <Box>

            {/* SECTION A: Current Rate Card */}
            <SectionCard>
                <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Box>
                            <Typography sx={{ color: "#888", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: 1 }}>
                                Current orchestration rate
                            </Typography>
                            <Typography sx={{ color: "#12c099", fontWeight: 800, fontSize: "3.5rem", lineHeight: 1 }}>
                                {Number(resolvedCurrent.rate).toFixed(resolvedCurrent.rate % 1 === 0 ? 0 : 2)}%
                            </Typography>
                            <Typography sx={{ color: "#bbb", mt: 1, fontSize: "0.95rem", maxWidth: 600 }}>
                                {describeMode(mode, resolvedCurrent, currentContractYearSpendUSD, contractYear)}
                            </Typography>
                        </Box>

                        <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => {
                                const el = document.getElementById("mode-selector");
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                            sx={outlinedMintSx}
                        >
                            Edit
                        </Button>
                    </Box>
                </CardContent>
            </SectionCard>

            {/* SECTION B: Annual Spend Tracker */}
            <SectionCard>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Annual Spend Tracker</Typography>
                    <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ color: "#888", fontSize: "0.85rem" }}>
                                Current contract year
                            </Typography>
                            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.6rem" }}>
                                {fmtUSD(currentContractYearSpendUSD)}
                            </Typography>
                            <Typography sx={{ color: "#888", fontSize: "0.8rem" }}>
                                {formatContractYearRange(contractAnniversary)}
                            </Typography>
                            {spendTiers.length === 0 ? (
                                <Typography sx={{ color: "#666", fontSize: "0.85rem", mt: 1, fontStyle: 'italic' }}>
                                    Tier-based pricing not configured. Using platform default.
                                </Typography>
                            ) : (
                                <Typography sx={{ color: "#666", fontSize: "0.85rem", mt: 1 }}>
                                    {fmtUSD(currentContractYearSpendUSD)} / {spendTiers.length} tier {spendTiers.length === 1 ? 'band' : 'bands'} configured
                                </Typography>
                            )}
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography sx={{ color: "#888", fontSize: "0.85rem" }}>
                                Lifetime on platform
                            </Typography>
                            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.6rem" }}>
                                {fmtUSD(lifetimeSpendUSD)}
                            </Typography>
                            <Typography sx={{ color: "#888", fontSize: "0.8rem" }}>
                                Cumulative across all contract years
                            </Typography>
                        </Grid>
                    </Grid>

                    {spendTiers.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography sx={{ color: "#888", fontSize: "0.8rem", mb: 1 }}>
                                Spend progress through configured tiers
                            </Typography>
                            <TierProgressBar tiers={spendTiers} spend={currentContractYearSpendUSD} />
                        </Box>
                    )}
                </CardContent>
            </SectionCard>

            {/* SECTION C: Configuration Mode Selector */}
            <SectionCard id="mode-selector">
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Configuration Mode</Typography>
                    <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

                    <FormControl>
                        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
                            {MODE_OPTIONS.map((opt) => (
                                <FormControlLabel
                                    key={opt.value}
                                    value={opt.value}
                                    control={<Radio sx={{ color: "#666", '&.Mui-checked': { color: '#12c099' } }} />}
                                    label={<Typography sx={{ color: "#ddd" }}>{opt.label}</Typography>}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>

                    <Box sx={{ mt: 3 }}>
                        {mode === 'platform_default' && (
                            <Box sx={{ p: 2, backgroundColor: "#151515", borderRadius: 2, border: "1px solid rgba(18,192,153,0.2)" }}>
                                <Typography sx={{ color: "#bbb" }}>
                                    This org will be charged the platform default rate of 15% on all spend. No further config needed.
                                </Typography>
                            </Box>
                        )}

                        {mode === 'flat_override' && (
                            <Box sx={{ maxWidth: 320 }}>
                                <Typography sx={{ color: "#aaa", mb: 0.5 }}>Custom rate (%)</Typography>
                                <TextField
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={flatRate}
                                    onChange={(e) => setFlatRate(e.target.value)}
                                    placeholder="e.g. 10"
                                    sx={inputSx}
                                />
                                <Typography sx={{ color: "#666", fontSize: "0.8rem", mt: 0.5 }}>
                                    e.g. 10 for 10% flat. Applies to all spend regardless of volume.
                                </Typography>
                            </Box>
                        )}

                        {(mode === 'spend_tier' || mode === 'spend_and_time') && (
                            <SpendTierEditor
                                tiers={spendTiers}
                                onChange={handleSpendBandChange}
                                onAdd={handleAddSpendBand}
                                onRemove={handleRemoveSpendBand}
                                onApplySuggested={openApplySuggested}
                            />
                        )}

                        {(mode === 'time_ramp' || mode === 'spend_and_time') && (
                            <Box sx={{ mt: mode === 'spend_and_time' ? 4 : 0 }}>
                                <TimeRampEditor
                                    ramps={timeRamp}
                                    onChange={handleTimeBandChange}
                                    onAdd={handleAddTimeBand}
                                    onRemove={handleRemoveTimeBand}
                                    contractAnniversary={contractAnniversary}
                                />
                            </Box>
                        )}

                        {mode === 'spend_and_time' && (
                            <Box sx={{ mt: 2, p: 1.5, backgroundColor: "#151515", borderRadius: 2 }}>
                                <Typography sx={{ color: "#bbb", fontSize: "0.85rem" }}>
                                    System applies whichever resolved rate is LOWER (customer-favorable).
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </SectionCard>

            {/* SECTION D: Contract Anniversary */}
            <SectionCard>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Contract Anniversary</Typography>
                    <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <EventIcon sx={{ color: "#12c099" }} />
                        <TextField
                            type="date"
                            size="small"
                            value={contractAnniversary}
                            onChange={(e) => setContractAnniversary(e.target.value)}
                            sx={{ ...inputSx, minWidth: 200 }}
                            InputLabelProps={{ shrink: true }}
                        />
                        <Typography sx={{ color: "#888", fontSize: "0.85rem", maxWidth: 500 }}>
                            Annual spend totals reset on this date. Time-based ramps anchor here.
                        </Typography>
                    </Box>
                </CardContent>
            </SectionCard>

            {/* SECTION E: Scenario Simulator */}
            <SectionCard>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Scenario Simulator</Typography>
                    <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

                    <Box sx={{ maxWidth: 320, mb: 2 }}>
                        <Typography sx={{ color: "#aaa", mb: 0.5 }}>Hypothetical annual spend ($)</Typography>
                        <TextField
                            type="number"
                            size="small"
                            fullWidth
                            value={simSpend}
                            onChange={(e) => setSimSpend(e.target.value)}
                            sx={inputSx}
                        />
                    </Box>

                    <Box sx={{ p: 2, backgroundColor: "#0d0d0d", borderRadius: 2, border: "1px solid rgba(18,192,153,0.15)" }}>
                        <Typography sx={{ color: "#888", fontSize: "0.85rem", mb: 1 }}>
                            At {fmtUSD(simSpend)} annual spend with the current config:
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Typography sx={{ color: "#888", fontSize: "0.8rem" }}>Effective rate</Typography>
                                <Typography sx={{ color: "#12c099", fontWeight: 700, fontSize: "1.8rem" }}>
                                    {Number(simResult.rate).toFixed(simResult.rate % 1 === 0 ? 0 : 2)}%
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography sx={{ color: "#888", fontSize: "0.8rem" }}>Projected platform fee</Typography>
                                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.8rem" }}>
                                    {fmtUSD(simulatedFee)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography sx={{ color: "#888", fontSize: "0.8rem" }}>Reason</Typography>
                                <Typography sx={{ color: "#ddd", fontSize: "0.9rem", mt: 1 }}>
                                    {simResult.reason}
                                </Typography>
                            </Grid>
                        </Grid>

                        {simResult.alt && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                                <Typography sx={{ color: "#888", fontSize: "0.8rem", mb: 1 }}>
                                    Spend + time breakdown
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography sx={{ color: "#bbb", fontSize: "0.85rem" }}>
                                            Spend-based: <strong>{Number(simResult.alt.spendRate).toFixed(2)}%</strong> ({simResult.alt.spendReason})
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography sx={{ color: "#bbb", fontSize: "0.85rem" }}>
                                            Time-based: <strong>{Number(simResult.alt.timeRate).toFixed(2)}%</strong> ({simResult.alt.timeReason})
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </SectionCard>

            {/* SECTION F: Change Log */}
            <SectionCard>
                <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <HistoryIcon sx={{ color: "#12c099" }} />
                        <Typography variant="h6">Change Log</Typography>
                    </Box>
                    <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

                    {changeLog.length === 0 ? (
                        <Typography sx={{ color: "#666", fontStyle: "italic" }}>
                            No configuration changes yet.
                        </Typography>
                    ) : (
                        <Box>
                            {changeLog.map((entry, idx) => (
                                <Box key={idx} sx={{ mb: 2, pb: 2, borderBottom: idx < changeLog.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                        <Chip
                                            label={entry.mode}
                                            size="small"
                                            sx={{ backgroundColor: "#12c099", color: "#000", fontWeight: 600 }}
                                        />
                                        <Typography sx={{ color: "#888", fontSize: "0.8rem" }}>
                                            {new Date(entry.timestamp).toLocaleString()}
                                        </Typography>
                                        <Typography sx={{ color: "#888", fontSize: "0.8rem" }}>
                                            · {entry.actor}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ color: "#ddd", fontSize: "0.9rem" }}>
                                        {entry.summary}
                                    </Typography>
                                    {entry.reason && (
                                        <Typography sx={{ color: "#888", fontSize: "0.85rem", mt: 0.5, fontStyle: "italic" }}>
                                            Reason: {entry.reason}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                </CardContent>
            </SectionCard>

            {/* Validation warnings/errors */}
            {(validation.errors.length > 0 || validation.warnings.length > 0) && (
                <SectionCard>
                    <CardContent>
                        {validation.errors.map((err, i) => (
                            <Box key={`e${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <WarningAmberIcon sx={{ color: '#ff6b6b' }} />
                                <Typography sx={{ color: '#ff6b6b', fontSize: '0.9rem' }}>{err}</Typography>
                            </Box>
                        ))}
                        {validation.warnings.map((w, i) => (
                            <Box key={`w${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <WarningAmberIcon sx={{ color: '#f5b945' }} />
                                <Typography sx={{ color: '#f5b945', fontSize: '0.9rem' }}>{w}</Typography>
                            </Box>
                        ))}
                    </CardContent>
                </SectionCard>
            )}

            {/* SECTION F2 (lifted from Program tab): Wellness Program Pricing & Margin */}
            <WellnessProgramPricing />

            {/* SECTION G: Save */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, mb: 6 }}>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveClick}
                    sx={{ ...mintButtonSx, fontSize: '1rem', px: 4, py: 1.2 }}
                >
                    Save configuration
                </Button>
            </Box>

            {/* Apply suggested tiers confirm */}
            <Dialog
                open={applySuggestedOpen}
                onClose={() => setApplySuggestedOpen(false)}
                PaperProps={{ sx: { backgroundColor: "#181818", color: "#fff" } }}
            >
                <DialogTitle sx={{ color: "#fff" }}>Apply suggested tiers?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "#bbb" }}>
                        This will overwrite the existing {spendTiers.length} spend tier band{spendTiers.length === 1 ? '' : 's'} with the 5-band suggested preset (15% / 13% / 11% / 9% / 8%).
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApplySuggestedOpen(false)} sx={{ color: "#888" }}>Cancel</Button>
                    <Button onClick={applySuggested} sx={mintButtonSx}>Apply</Button>
                </DialogActions>
            </Dialog>

            {/* Save confirm */}
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                PaperProps={{ sx: { backgroundColor: "#181818", color: "#fff", minWidth: 460 } }}
            >
                <DialogTitle sx={{ color: "#fff" }}>Confirm save</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "#bbb", mb: 2 }}>
                        Save orchestration fee configuration for <strong>{MOCK_ORG.orgName}</strong>?
                    </Typography>
                    <Typography sx={{ color: "#aaa", mb: 0.5, fontSize: "0.85rem" }}>
                        Reason for change (optional, written to change log)
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        value={saveReason}
                        onChange={(e) => setSaveReason(e.target.value)}
                        placeholder="e.g. negotiated rate per Q2 contract"
                        sx={inputSx}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} sx={{ color: "#888" }}>Cancel</Button>
                    <Button onClick={handleSaveConfirm} sx={mintButtonSx}>Save</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3500}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={toast.severity}
                    onClose={() => setToast({ ...toast, open: false })}
                    sx={{ width: '100%' }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

// -----------------------------------------------------------------------------
//  SUB-COMPONENTS
// -----------------------------------------------------------------------------

function SpendTierEditor({ tiers, onChange, onAdd, onRemove, onApplySuggested }) {
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ color: "#ddd", fontWeight: 600 }}>Tier bands</Typography>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AutoFixHighIcon />}
                    onClick={onApplySuggested}
                    sx={mintButtonSx}
                >
                    Apply suggested tiers
                </Button>
            </Box>

            <TableContainer component={Box} sx={{ backgroundColor: "#0d0d0d", borderRadius: 2, mb: 1 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={tableHeadSx}>Floor ($)</TableCell>
                            <TableCell sx={tableHeadSx}>Ceiling ($) — blank = ∞</TableCell>
                            <TableCell sx={tableHeadSx}>Rate (%)</TableCell>
                            <TableCell sx={tableHeadSx} align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tiers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} sx={{ ...tableCellSx, color: "#666", fontStyle: 'italic', textAlign: 'center', py: 3 }}>
                                    No tier bands. Click "Apply suggested tiers" or "Add band" below.
                                </TableCell>
                            </TableRow>
                        ) : tiers.map((t, idx) => (
                            <TableRow key={idx}>
                                <TableCell sx={tableCellSx}>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={t.floorUSD}
                                        onChange={(e) => onChange(idx, 'floorUSD', e.target.value)}
                                        sx={inputSx}
                                    />
                                </TableCell>
                                <TableCell sx={tableCellSx}>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={t.ceilingUSD === null ? "" : t.ceilingUSD}
                                        onChange={(e) => onChange(idx, 'ceilingUSD', e.target.value)}
                                        placeholder="∞"
                                        sx={inputSx}
                                    />
                                </TableCell>
                                <TableCell sx={tableCellSx}>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={t.rate}
                                        onChange={(e) => onChange(idx, 'rate', e.target.value)}
                                        sx={inputSx}
                                    />
                                </TableCell>
                                <TableCell sx={tableCellSx} align="right">
                                    <Tooltip title="Remove band">
                                        <IconButton onClick={() => onRemove(idx)} size="small" sx={{ color: '#888', '&:hover': { color: '#ff6b6b' } }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Button
                onClick={onAdd}
                startIcon={<AddIcon />}
                size="small"
                sx={{ color: "#12c099", textTransform: "none" }}
            >
                Add band
            </Button>

            <Typography sx={{ color: "#888", fontSize: "0.8rem", mt: 1 }}>
                Annual spend resets on contract anniversary. System applies the rate matching the band the spend lands in.
            </Typography>
            <Typography sx={{ color: "#666", fontSize: "0.75rem", mt: 0.5, fontStyle: 'italic' }}>
                Default suggested tiers: 15% / 13% / 11% / 9% / 8% across $0-$500K / $500K-$2M / $2M-$10M / $10M-$25M / $25M+. Click "Apply suggested tiers" to populate.
            </Typography>
        </Box>
    );
}

function TimeRampEditor({ ramps, onChange, onAdd, onRemove, contractAnniversary }) {
    return (
        <Box>
            <Typography sx={{ color: "#ddd", fontWeight: 600, mb: 1 }}>Contract-year bands</Typography>

            <TableContainer component={Box} sx={{ backgroundColor: "#0d0d0d", borderRadius: 2, mb: 1 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={tableHeadSx}>Year start</TableCell>
                            <TableCell sx={tableHeadSx}>Year end</TableCell>
                            <TableCell sx={tableHeadSx}>Rate (%)</TableCell>
                            <TableCell sx={tableHeadSx} align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ramps.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} sx={{ ...tableCellSx, color: "#666", fontStyle: 'italic', textAlign: 'center', py: 3 }}>
                                    No ramp bands. Click "Add band" below.
                                </TableCell>
                            </TableRow>
                        ) : ramps.map((r, idx) => (
                            <TableRow key={idx}>
                                <TableCell sx={tableCellSx}>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={r.yearStart}
                                        onChange={(e) => onChange(idx, 'yearStart', e.target.value)}
                                        sx={inputSx}
                                    />
                                </TableCell>
                                <TableCell sx={tableCellSx}>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={r.yearEnd === null ? "" : r.yearEnd}
                                        onChange={(e) => onChange(idx, 'yearEnd', e.target.value)}
                                        sx={inputSx}
                                    />
                                </TableCell>
                                <TableCell sx={tableCellSx}>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={r.rate}
                                        onChange={(e) => onChange(idx, 'rate', e.target.value)}
                                        sx={inputSx}
                                    />
                                </TableCell>
                                <TableCell sx={tableCellSx} align="right">
                                    <Tooltip title="Remove band">
                                        <IconButton onClick={() => onRemove(idx)} size="small" sx={{ color: '#888', '&:hover': { color: '#ff6b6b' } }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Button
                onClick={onAdd}
                startIcon={<AddIcon />}
                size="small"
                sx={{ color: "#12c099", textTransform: "none" }}
            >
                Add band
            </Button>

            <Typography sx={{ color: "#888", fontSize: "0.8rem", mt: 1 }}>
                Rate changes by contract year, anchored to {contractAnniversary}. Year start/end are 1-indexed.
            </Typography>
        </Box>
    );
}

function TierProgressBar({ tiers, spend }) {
    const sorted = [...tiers].sort((a, b) => Number(a.floorUSD) - Number(b.floorUSD));
    const last = sorted[sorted.length - 1];
    const cap = last && (last.ceilingUSD === null || last.ceilingUSD === "")
        ? Math.max(Number(last.floorUSD) * 1.5, spend * 1.1, 1)
        : Math.max(Number(last.ceilingUSD) || 1, spend * 1.1);

    const pct = Math.min(100, Math.max(0, (spend / cap) * 100));

    return (
        <Box sx={{ position: 'relative' }}>
            <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: "#222",
                    '& .MuiLinearProgress-bar': { backgroundColor: "#12c099" }
                }}
            />
            <Box sx={{ display: 'flex', mt: 0.5, position: 'relative', height: 18 }}>
                {sorted.map((t, idx) => {
                    const floorPct = (Number(t.floorUSD) / cap) * 100;
                    return (
                        <Box
                            key={idx}
                            sx={{
                                position: 'absolute',
                                left: `${floorPct}%`,
                                fontSize: '0.7rem',
                                color: '#888',
                                transform: 'translateX(-50%)'
                            }}
                        >
                            {fmtUSDCompact(Number(t.floorUSD))}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

// -----------------------------------------------------------------------------
//  TEXT BUILDERS
// -----------------------------------------------------------------------------

function describeMode(mode, resolved, currentSpend, contractYear) {
    if (mode === 'platform_default') {
        return "Platform default rate. Hidden in invoices. No custom config.";
    }
    if (mode === 'flat_override') {
        return `Custom flat rate set ${changeDateMsg()}.`;
    }
    if (mode === 'spend_tier') {
        return `Spend-tier pricing. Current rate based on ${fmtUSD(currentSpend)} YTD spend.`;
    }
    if (mode === 'time_ramp') {
        return `Time-based ramp. Year ${contractYear} of contract.`;
    }
    if (mode === 'spend_and_time') {
        return "Hybrid pricing. Whichever rate is lower applies.";
    }
    return resolved.reason || "";
}

function changeDateMsg() {
    // V1 placeholder. T6 will surface the actual last-edit timestamp + admin.
    return "by current admin (timestamps appear in change log)";
}

function describeChange(config) {
    if (config.mode === 'platform_default') {
        return "Set to platform default (15%).";
    }
    if (config.mode === 'flat_override') {
        return `Flat override at ${config.flatRate}%.`;
    }
    if (config.mode === 'spend_tier') {
        return `Spend-tier pricing with ${config.spendTiers.length} band${config.spendTiers.length === 1 ? '' : 's'}.`;
    }
    if (config.mode === 'time_ramp') {
        return `Time-ramp pricing with ${config.timeRamp.length} band${config.timeRamp.length === 1 ? '' : 's'}.`;
    }
    if (config.mode === 'spend_and_time') {
        return `Hybrid pricing: ${config.spendTiers.length} spend tier${config.spendTiers.length === 1 ? '' : 's'} + ${config.timeRamp.length} time band${config.timeRamp.length === 1 ? '' : 's'}.`;
    }
    return "Configuration updated.";
}

function formatContractYearRange(anniversaryISO) {
    if (!anniversaryISO) return "";
    const start = new Date(anniversaryISO);
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${start.toLocaleDateString('en-US', opts)} → ${end.toLocaleDateString('en-US', opts)}`;
}

// -----------------------------------------------------------------------------
//  WELLNESS PROGRAM PRICING & MARGIN  (lifted from EnterpriseProgramTab)
// -----------------------------------------------------------------------------
// Conceptually distinct from orchestration fees (which is a percentage on
// third-party spend). This block estimates monthly margin on wellness
// programming: target margin, per-employee rate, weekly cadence, and
// instructor cost. Kept on this tab so live pricing logic is not lost.

const PRICING_EMPLOYEE_COUNT = 1240;

function WellnessProgramPricing() {
    const [targetMargin, setTargetMargin] = useState(55);
    const [perEmployeeRate, setPerEmployeeRate] = useState(22);
    const [cadencePerWeek, setCadencePerWeek] = useState(3);
    const [instructorCostPerSession, setInstructorCostPerSession] = useState(120);
    const [managedByWhook, setManagedByWhook] = useState(false);
    const [pricingLogs, setPricingLogs] = useState([]);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
    const [pendingOptimized, setPendingOptimized] = useState(null);

    const summary = useMemo(() => {
        const sessionsPerMonth = cadencePerWeek * 4;
        const monthlyRevenue = PRICING_EMPLOYEE_COUNT * perEmployeeRate;
        const monthlyCost = sessionsPerMonth * instructorCostPerSession;
        const margin =
            monthlyRevenue > 0
                ? Math.round(((monthlyRevenue - monthlyCost) / monthlyRevenue) * 100)
                : 0;
        return { sessionsPerMonth, monthlyRevenue, monthlyCost, margin };
    }, [perEmployeeRate, cadencePerWeek, instructorCostPerSession]);

    const handleTargetMarginChange = (event, value) => {
        setTargetMargin(value);
        const baseRate = 20;
        const newRate = Math.round(baseRate + (value - 50) * 0.4);
        const newCadence = Math.min(5, Math.max(1, Math.round(3 + (value - 55) / 25)));
        const newInstructorCost = Math.round(130 - (value - 50) * 0.3);
        setPerEmployeeRate(newRate);
        setCadencePerWeek(newCadence);
        setInstructorCostPerSession(newInstructorCost);
    };

    const handleWhookOptimize = () => {
        const optimizedRate = Math.round(18 + targetMargin * 0.45);
        const optimizedCadence = Math.min(5, Math.max(1, Math.round(2 + targetMargin / 25)));
        const optimizedInstructorCost = Math.round(110 - (targetMargin - 50) * 0.5);
        setPerEmployeeRate(optimizedRate);
        setCadencePerWeek(optimizedCadence);
        setInstructorCostPerSession(optimizedInstructorCost);
        setPendingOptimized({
            targetMargin,
            perEmployeeRate: optimizedRate,
            cadencePerWeek: optimizedCadence,
            instructorCostPerSession: optimizedInstructorCost,
        });
        setSaveConfirmOpen(true);
    };

    const handleSaveConfirmYes = () => {
        if (pendingOptimized) {
            setPricingLogs((prev) => [...prev, { timestamp: new Date().toISOString(), ...pendingOptimized }]);
        }
        setPendingOptimized(null);
        setSaveConfirmOpen(false);
    };

    const handleSaveConfirmNo = () => {
        setPendingOptimized(null);
        setSaveConfirmOpen(false);
    };

    return (
        <SectionCard>
            <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="h6">Wellness Program Pricing & Margin</Typography>
                        <SharedInfoIcon text="Margin estimator for wellness programming. Separate from orchestration fees: estimates monthly margin from per-employee rate, cadence, and instructor cost. Dummy only." />
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={managedByWhook}
                                onChange={(e) => setManagedByWhook(e.target.checked)}
                                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#12c099' } }}
                            />
                        }
                        label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <ScienceIcon sx={{ fontSize: 18, color: "#12c099" }} />
                                <Typography sx={{ fontSize: "0.85rem" }}>Let Whook manage pricing</Typography>
                            </Box>
                        }
                    />
                </Box>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

                <Box sx={{ mb: 3 }}>
                    <Typography sx={{ mb: 1 }}>Target Gross Margin</Typography>
                    <Slider
                        value={targetMargin}
                        onChange={handleTargetMarginChange}
                        min={20}
                        max={80}
                        step={1}
                        valueLabelDisplay="on"
                        sx={{
                            '& .MuiSlider-thumb': { color: '#12c099' },
                            '& .MuiSlider-track': { color: '#12c099' },
                            '& .MuiSlider-rail': { color: '#444' }
                        }}
                    />
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                        <Typography sx={{ mb: 0.5, color: "#aaa" }}>Per-employee rate (est. monthly)</Typography>
                        <TextField
                            type="number"
                            size="small"
                            fullWidth
                            value={perEmployeeRate}
                            onChange={(e) => setPerEmployeeRate(Number(e.target.value || 0))}
                            InputProps={{
                                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                                sx: { backgroundColor: "#161616", color: "#fff" }
                            }}
                            sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}
                            disabled={managedByWhook}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography sx={{ mb: 0.5, color: "#aaa" }}>Weekly cadence (sessions/week)</Typography>
                        <TextField
                            type="number"
                            size="small"
                            fullWidth
                            value={cadencePerWeek}
                            onChange={(e) => setCadencePerWeek(Number(e.target.value || 0))}
                            InputProps={{ sx: { backgroundColor: "#161616", color: "#fff" } }}
                            sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}
                            disabled={managedByWhook}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography sx={{ mb: 0.5, color: "#aaa" }}>Instructor cost per session</Typography>
                        <TextField
                            type="number"
                            size="small"
                            fullWidth
                            value={instructorCostPerSession}
                            onChange={(e) => setInstructorCostPerSession(Number(e.target.value || 0))}
                            InputProps={{
                                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                                sx: { backgroundColor: "#161616", color: "#fff" }
                            }}
                            sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}
                            disabled={managedByWhook}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ p: 2, borderRadius: 2, backgroundColor: "#0d0d0d", border: "1px solid rgba(18,192,153,0.15)", mb: managedByWhook ? 3 : 0 }}>
                    <Typography sx={{ color: "#888", fontSize: "0.85rem", mb: 1 }}>
                        Margin summary
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ color: "#888", fontSize: "0.8rem" }}>Sessions / month</Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: "1.5rem" }}>{summary.sessionsPerMonth}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ color: "#888", fontSize: "0.8rem" }}>Est. monthly revenue</Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: "1.5rem" }}>${summary.monthlyRevenue.toLocaleString()}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ color: "#888", fontSize: "0.8rem" }}>Est. margin</Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: "1.5rem", color: summary.margin >= targetMargin ? "#12c099" : "#f5b945" }}>
                                {summary.margin}%
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {managedByWhook && (
                    <Box sx={{ p: 2, borderRadius: 2, backgroundColor: "#151515" }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
                            <ScienceIcon sx={{ color: "#12c099" }} />
                            <Typography sx={{ fontWeight: 600 }}>Auto Pricing Intelligence (Whook)</Typography>
                        </Box>
                        <Typography sx={{ color: "#aaa", fontSize: "0.9rem", mb: 2 }}>
                            Whook will adjust cadence, per-employee rate, and instructor costs to move this account toward
                            the target margin using live engagement and cost signals.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleWhookOptimize}
                            sx={{ backgroundColor: "#12c099", color: "#000", fontWeight: 700, textTransform: "none" }}
                        >
                            Optimize Pricing with Whook
                        </Button>
                        {pricingLogs.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography sx={{ fontSize: "0.8rem", color: "#888", mb: 1 }}>
                                    Recent pricing decisions saved to Whook learning logs:
                                </Typography>
                                {pricingLogs.slice(-3).map((log, idx) => (
                                    <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.8rem", color: "#bbb", mb: 0.5 }}>
                                        <CheckCircleIcon sx={{ fontSize: 14, color: "#12c099" }} />
                                        <span>
                                            {new Date(log.timestamp).toLocaleString()} - Margin target {log.targetMargin}%, ${log.perEmployeeRate}/emp, {log.cadencePerWeek}x/week, ${log.instructorCostPerSession}/session
                                        </span>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </CardContent>

            <SharedModal
                open={saveConfirmOpen}
                onClose={handleSaveConfirmNo}
                title="Save to Whook learning logs?"
                size="sm"
                footer={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" onClick={handleSaveConfirmNo} sx={{ color: '#888', textTransform: 'none' }}>
                            Discard
                        </Button>
                        <Button variant="contained" onClick={handleSaveConfirmYes} sx={{ backgroundColor: '#12c099', color: '#000', fontWeight: 700, textTransform: 'none' }}>
                            Save
                        </Button>
                    </Box>
                }
            >
                <Typography sx={{ fontSize: '13px', color: '#bbb' }}>
                    Save this optimized pricing decision into the local Whook learning-log dummy state? Nothing leaves the page.
                </Typography>
            </SharedModal>
        </SectionCard>
    );
}
